/**
 * Slurp backend API client + WS singleton.
 *
 * One source of truth for every number the UI displays. No direct mockTokens
 * imports — everything that reads about tokens/trades/leaderboard goes
 * through here.
 */

const API_BASE =
  process.env.REACT_APP_SLURP_API_URL || "http://localhost:4000";
const WS_URL =
  process.env.REACT_APP_SLURP_WS_URL ||
  API_BASE.replace(/^http/, "ws") + "/ws";

// In-memory cache for profile reads. Survives until page reload.
// `null` = known to have no profile (server returned 404). `undefined`
// = never fetched. saveProfile invalidates the wallet's entry so the
// edit modal sees its own changes immediately.
const profileMemo = new Map();
export const profileCache = {
  get: (wallet) => profileMemo.get(wallet),
  set: (wallet, profile) => profileMemo.set(wallet, profile),
  invalidate: (wallet) => profileMemo.delete(wallet),
};

async function getJson(path, opts = {}) {
  const { headers: extraHeaders, timeoutMs = 15000, ...rest } = opts;
  // Defensive timeout — fetch with no AbortSignal will hang forever on
  // a CORS-preflight failure or a stalled connection. 15s is plenty for
  // any of our reads/writes; the launch receipt POST loop relies on
  // fetch *throwing* between retries, so a hung fetch breaks the retry
  // logic entirely.
  const controller = typeof AbortController !== "undefined" ? new AbortController() : null;
  const timer = controller ? setTimeout(() => controller.abort(), timeoutMs) : null;
  let r;
  try {
    r = await fetch(`${API_BASE}${path}`, {
      ...rest,
      signal: controller?.signal,
      headers: { "Content-Type": "application/json", ...(extraHeaders || {}) },
    });
  } finally {
    if (timer) clearTimeout(timer);
  }
  if (!r.ok) {
    let detail;
    try {
      detail = await r.json();
    } catch {
      detail = await r.text();
    }
    throw new Error(
      `Slurp API ${r.status} ${path}: ${typeof detail === "string" ? detail : JSON.stringify(detail)}`
    );
  }
  return r.json();
}

export const slurpApi = {
  listTokens: ({ sort = "volume", limit = 50, offset = 0, search = "" } = {}) => {
    const q = new URLSearchParams();
    q.set("sort", sort);
    q.set("limit", String(limit));
    q.set("offset", String(offset));
    if (search) q.set("search", search);
    return getJson(`/api/tokens?${q.toString()}`);
  },
  getToken: (mint) => getJson(`/api/tokens/${mint}`),
  getTokenTrades: (mint, { limit = 50, offset = 0 } = {}) =>
    getJson(`/api/tokens/${mint}/trades?limit=${limit}&offset=${offset}`),
  getTokenHistory: (mint, range = "24h") =>
    getJson(`/api/tokens/${mint}/history?range=${encodeURIComponent(range)}`),
  getTokenHolders: (mint, { limit = 50 } = {}) =>
    getJson(`/api/tokens/${mint}/holders?limit=${limit}`),
  getPortfolio: (owner) => getJson(`/api/portfolio/${owner}`),
  // Server-side batched read of pending claim fees for every launch this
  // wallet owns. Solves the LaunchCardWithClaim fan-out problem (each
  // card was firing 2 Helius RPC calls on mount, 40+ in parallel for
  // creators with many launches → 429 storm). Backend caches 30s.
  getPendingFees: (owner) => getJson(`/api/portfolio/${owner}/pending-fees`),
  getWalletTrades: (owner, { limit = 50, offset = 0 } = {}) =>
    getJson(`/api/portfolio/${owner}/trades?limit=${limit}&offset=${offset}`),

  // ─── Wallet profiles ───────────────────────────────────────────
  // Public reads — anyone can view any wallet's profile.
  // Memo cache so navigating to /profile/<wallet> after the welcome
  // modal already fetched it shows the username + pfp instantly
  // instead of flashing shortAddr → @username. 404s are cached as
  // null too so we don't spam the backend for profileless wallets.
  getProfile: (wallet) => {
    const cached = profileMemo.get(wallet);
    if (cached !== undefined) {
      return cached === null
        ? Promise.reject(new Error(`Slurp API 404 /api/profiles/${wallet}: no_profile`))
        : Promise.resolve({ profile: cached });
    }
    return getJson(`/api/profiles/${wallet}`)
      .then((r) => { profileMemo.set(wallet, r.profile); return r; })
      .catch((err) => {
        if (String(err?.message || "").includes("404")) profileMemo.set(wallet, null);
        throw err;
      });
  },
  // Hard refetch — bypasses the cache. Use after a save or when you
  // know the profile changed.
  refreshProfile: (wallet) =>
    getJson(`/api/profiles/${wallet}`)
      .then((r) => { profileMemo.set(wallet, r.profile); return r; })
      .catch((err) => {
        if (String(err?.message || "").includes("404")) profileMemo.set(wallet, null);
        throw err;
      }),
  getProfileByUsername: (username) =>
    getJson(`/api/profiles/by-username/${encodeURIComponent(username)}`),
  // Substring search over usernames + exact wallet match. Used by the
  // profile-page search bar so users can jump to other profiles directly.
  searchProfiles: (q, limit = 20) =>
    getJson(`/api/profiles/search?q=${encodeURIComponent(q)}&limit=${limit}`),
  // Username availability check — used live as the user types.
  // `wallet` is the connected wallet so re-saving your own current
  // username doesn't show as taken.
  checkProfileUsername: (username, wallet) =>
    getJson(`/api/profiles/check-username`, {
      method: "POST",
      body: JSON.stringify({ username, wallet }),
    }),
  /**
   * Save a profile. `sigHeaders` must contain ed25519 signature headers
   * produced by the wallet adapter — see `signProfileMessage` below.
   */
  saveProfile: (wallet, body, sigHeaders) =>
    getJson(`/api/profiles/${wallet}`, {
      method: "POST",
      headers: sigHeaders,
      body: JSON.stringify(body),
    }).then((r) => {
      // Update the cache immediately so the next render sees the new value
      // (the saveProfile callsites typically re-fetch but the cache also
      // matters for any other component that already mounted).
      profileMemo.set(wallet, r.profile);
      return r;
    }),
  // X (Twitter) verification — build the redirect URL the caller assigns
  // to window.location. Backend verifies the wallet sig, then 302's to
  // X's OAuth consent screen.
  xVerifyStartUrl: (wallet, ts, sig) =>
    `${API_BASE}/api/auth/x/start?wallet=${encodeURIComponent(wallet)}&ts=${ts}&sig=${encodeURIComponent(sig)}`,
  // Clear the verified flag (e.g. to re-verify with a different X account).
  xVerifyUnlink: (wallet, sigHeaders) =>
    getJson(`/api/auth/x/unlink/${wallet}`, {
      method: "POST",
      headers: sigHeaders,
    }).then((r) => {
      profileMemo.delete(wallet);
      return r;
    }),
  // Follow / unfollow / stats. Follow + unfollow need a wallet signature
  // from the FOLLOWER (not the URL wallet); see requireFollowerSig server-side.
  getFollowStats: (wallet, viewer) => {
    const q = viewer ? `?viewer=${encodeURIComponent(viewer)}` : "";
    return getJson(`/api/profiles/${wallet}/follow-stats${q}`);
  },
  // List of wallets that follow this profile (or that this profile follows).
  // Returns { list: [{ wallet, username, image_url, created_at }] }.
  getFollowers: (wallet, { limit = 100 } = {}) =>
    getJson(`/api/profiles/${wallet}/followers?limit=${limit}`),
  getFollowing: (wallet, { limit = 100 } = {}) =>
    getJson(`/api/profiles/${wallet}/following?limit=${limit}`),
  followProfile: (followee, sigHeaders) =>
    getJson(`/api/profiles/${followee}/follow`, {
      method: "POST",
      headers: sigHeaders,
    }),
  unfollowProfile: (followee, sigHeaders) =>
    getJson(`/api/profiles/${followee}/follow`, {
      method: "DELETE",
      headers: sigHeaders,
    }),
  // Notifications — public read for now; read-state tracked client-side
  // via localStorage `slurp:last_seen_notif:{wallet}`.
  getNotifications: (wallet, { limit = 30 } = {}) =>
    getJson(`/api/profiles/${wallet}/notifications?limit=${limit}`),
  markNotificationsRead: (wallet) =>
    getJson(`/api/profiles/${wallet}/notifications/read`, { method: "POST" }),

  getLeaderboard: (timeframe = "24h", limit = 20) =>
    getJson(`/api/leaderboard?timeframe=${timeframe}&limit=${limit}`),
  // Hold-time-weighted leaderboard, rolling 7-day window. Returns
  // { leaderboard: [{ rank, wallet, score, updated_at }], window_days }.
  getLeaderboardScore: (limit = 100) =>
    getJson(`/api/leaderboard/score?limit=${limit}`),
  getStats: () => getJson("/api/stats"),
  // OHLC bars for the candle chart on the detail page. resolution is one of
  // 1m,5m,15m,30m,1h,4h,1d. Returns { bars, resolution, bucket_seconds }.
  getOhlc: (mint, { resolution = "1m", limit = 500, from, to } = {}) => {
    const q = new URLSearchParams({ resolution, limit: String(limit) });
    if (from != null) q.set("from", String(from));
    if (to != null) q.set("to", String(to));
    return getJson(`/api/ohlc/${mint}?${q.toString()}`);
  },
  postLaunch: (receipt) =>
    getJson("/api/launches", {
      method: "POST",
      body: JSON.stringify(receipt),
      // 5s blocking timeout. With both frontend and backend on Helius,
      // a confirmed-on-chain tx is visible to the backend essentially
      // instantly — the typical receipt POST resolves in <1s. If it
      // takes longer than 5s, something's actually wrong (slow backend,
      // network), and we hand off to the launchToken background retry
      // path so the UI can flip to "Launched!" without waiting.
      // The backend's internal poll still has its full 10s window; we
      // just stop blocking the user on a corner-case slow response.
      timeoutMs: 5000,
    }),

  /**
   * Ask the backend to sign a one-shot R2 upload URL. Returns
   * `{ uploadUrl, publicUrl, key }`. The frontend then PUTs the file
   * directly to `uploadUrl` and uses `publicUrl` as the image src.
   * Backend rate-limits per-IP and validates content-type + size, so
   * no wallet sig is required from the user.
   */
  signUpload: ({ contentType, size, prefix }) =>
    getJson("/api/uploads/sign", {
      method: "POST",
      body: JSON.stringify({ contentType, size, prefix }),
    }),
  postBurnReceipt: (mint, sig) =>
    getJson(`/api/tokens/${mint}/burn-receipt`, {
      method: "POST",
      body: JSON.stringify({ sig }),
    }),
  checkSymbol: (symbol, name = "") =>
    getJson(
      `/api/tokens/check-symbol?symbol=${encodeURIComponent(symbol)}` +
      (name ? `&name=${encodeURIComponent(name)}` : "")
    ),

  // Jackpot — public, no auth. Pot accumulates from 10% of every trade
  // fee on every slurp token; one random winner drawn monthly.
  getJackpotPot: () => getJson(`/api/jackpot/pot`),
  getJackpotWinners: () => getJson(`/api/jackpot/winners`),
};

// ─────────────────────────────────────────────────────────────
// WebSocket singleton — auto-reconnects, typed fan-out to subscribers.
// Callers use `slurpWs.subscribe((msg) => {…})` and get unsubscribe back.
// ─────────────────────────────────────────────────────────────
class SlurpWs {
  constructor() {
    this.listeners = new Set();
    this.ws = null;
    this.reconnectTimer = null;
    this.isConnected = false;
    this.reconnectAttempts = 0;
    this.connect();
  }

  connect() {
    try {
      this.ws = new WebSocket(WS_URL);
    } catch (err) {
      console.warn("[slurpWs] construction failed:", err);
      this.scheduleReconnect();
      return;
    }
    this.ws.onopen = () => {
      this.isConnected = true;
      // Reset the backoff counter on a successful connection so a long-lived
      // session that drops once doesn't carry stale backoff into its next
      // reconnect cycle.
      this.reconnectAttempts = 0;
      this.emit({ type: "__status", status: "connected" });
    };
    this.ws.onmessage = (event) => {
      let msg;
      try {
        msg = JSON.parse(event.data);
      } catch {
        return;
      }
      this.emit(msg);
    };
    this.ws.onclose = () => {
      this.isConnected = false;
      this.emit({ type: "__status", status: "disconnected" });
      this.scheduleReconnect();
    };
    this.ws.onerror = () => {
      // onclose will fire after, reconnect handled there.
    };
  }

  scheduleReconnect() {
    if (this.reconnectTimer) return;
    // Exponential backoff with jitter — fixed 3s reconnect from every
    // client at once causes a thundering herd against the API during
    // sustained outages (a single Railway redeploy means every connected
    // client hammers the new instance every 3s). Cap at 30s; jitter
    // spreads the herd across the window.
    const attempt = Math.min(this.reconnectAttempts, 5);
    const baseMs = Math.min(30_000, 1_000 * Math.pow(2, attempt));
    const jitterMs = Math.random() * 500;
    const delay = baseMs + jitterMs;
    this.reconnectAttempts += 1;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  }

  emit(msg) {
    for (const fn of this.listeners) {
      try {
        fn(msg);
      } catch (err) {
        console.error("[slurpWs] listener error:", err);
      }
    }
  }

  subscribe(fn) {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }
}

export const slurpWs = typeof window !== "undefined" ? new SlurpWs() : null;
